import { defineStore } from "pinia"
import { ref, computed } from "vue"
import { useRouter } from "vue-router"

import fp from "@/utils/lodash-fp"
import TutorService from "@/services/resources/TutorService"
import CourseService from "@/services/resources/CourseService"
import CertifyService from "@/services/resources/CertifyService"
import CommentService from "@/services/resources/CommentService"
import { MB_1 } from "@/utils/constants"

const serviceCourse = CourseService.build()
const serviceCertificate = CertifyService.build()
const serviceComments = CommentService.build()
const tutorService = TutorService.build()

// --- Types ---

interface CourseParams {
  courseId?: number | null
  moduleId?: number | null
  lessonId?: number | null
}

interface Course {
  title: string
  [key: string]: unknown
}

interface Module {
  id: number
  is_liberated?: boolean
  lessons_completed_count?: number
  lessons_count?: number
  [key: string]: unknown
}

interface Lesson {
  id: number
  module_id?: number
  is_completed?: boolean
  is_liberated?: boolean
  [key: string]: unknown
}

interface LoadingState {
  all: boolean
  lesson: boolean
  module: boolean
  course: boolean
  comments: boolean
}

interface SubmittingState {
  completion: boolean
  comment: boolean
  answer?: boolean
}

interface Comment {
  [key: string]: unknown
}

export const useCoursesStore = defineStore('courses', () => {
  const router = useRouter()

  // --- State ---
  const params = ref<CourseParams>({})
  const course = ref<Course>({ title: "" })
  const modules = ref<Module[]>([])
  const currentModuleOpened = ref<number | null>(null)
  const coursePercentage = ref(0)
  const currentLesson = ref<Record<string, unknown>>({})
  const currentModuleLessons = ref<Lesson[]>([])
  const currentLessonComments = ref<Comment[]>([])
  const lessonComments = ref<Comment[]>([])
  const prevLesson = ref<CourseParams | null>(null)
  const nextLesson = ref<CourseParams | null>(null)
  const loading = ref<LoadingState>({
    all: true,
    lesson: true,
    module: true,
    course: true,
    comments: true,
  })
  const submitting = ref<SubmittingState>({
    completion: false,
    comment: false,
  })
  const currentCourseSelect = ref<unknown[]>([])

  // --- Getters (computed) ---
  const getCourse = computed(() => course.value)
  const getCoursePercentage = computed(() => coursePercentage.value)
  const getModules = computed(() => modules.value)
  const getCurrentModuleLessons = computed(() => currentModuleLessons.value)
  const getCurrentLesson = computed(() => currentLesson.value)
  const getParams = computed(() => params.value)
  const getLoading = computed(() => loading.value)
  const getSubmitting = computed(() => submitting.value)
  const getPrevLesson = computed(() => prevLesson.value)
  const getNextLesson = computed(() => nextLesson.value)
  const getLessonComments = computed(() => lessonComments.value)
  const getCurrentModuleOpened = computed(() => currentModuleOpened.value)
  const getCurrentCourseSelect = computed(() => currentCourseSelect.value)

  // --- Internal helpers ---

  function clear() {
    params.value = {}
    course.value = { title: "" }
    modules.value = []
    currentModuleOpened.value = null
    coursePercentage.value = 0
    currentLesson.value = {}
    currentModuleLessons.value = []
    currentLessonComments.value = []
    lessonComments.value = []
    prevLesson.value = null
    nextLesson.value = null
    Object.assign(loading.value, { all: true, lesson: true, module: true, course: true, comments: true })
    Object.assign(submitting.value, { completion: false, comment: false })
    currentCourseSelect.value = []
  }

  // --- Actions ---

  async function actionCourseSelect() {
    const response = await serviceCourse.read(`/select`)
    currentCourseSelect.value = response
    return response
  }

  function actionCurrentLessonOpened(moduleId: number | null) {
    currentModuleOpened.value = moduleId ?? null
  }

  async function actionOpenLesson({
    courseId,
    moduleId,
    lessonId,
    orientation,
  }: {
    courseId: number
    moduleId?: number | null
    lessonId?: number | null
    orientation?: string
  }) {
    const next: CourseParams = {
      courseId: Number(courseId),
      lessonId: Number(lessonId) || null,
      moduleId: Number(moduleId) || null,
    }

    const currentParams = params.value
    let promises: Promise<unknown>[] = []

    try {
      if (currentParams.courseId !== courseId) {
        clear()
        promises.push(actionFetchCourse({ courseId: next.courseId! }))
        promises.push(actionFetchCoursePercentage({ courseId: next.courseId! }))
        await Promise.all(promises)
        promises = []
        next.moduleId = moduleId || (await actionNextModule({ modules: modules.value }))?.id
      }

      if (currentParams.moduleId !== next.moduleId) {
        promises.push(actionFetchModuleLessons({ courseId: next.courseId!, moduleId: next.moduleId! }))
      }

      if (!next.moduleId || !next.lessonId) {
        await Promise.all(promises)
        promises = []

        const lesson = await actionComputeCurrentLesson({ orientation })
        next.lessonId = lesson?.id

        await router.push({
          name: next.lessonId ? "watch-lesson" : next.moduleId ? "watch-module" : "watch",
          params: { ...next } as Record<string, string>,
        })

        actionCurrentLessonOpened(next.moduleId ?? null)
      }

      if (currentParams.lessonId !== next.lessonId) {
        promises.push(actionFetchLesson({ courseId: next.courseId!, moduleId: next.moduleId!, lessonId: next.lessonId! }))
      }

      await Promise.all(promises)
      params.value = next

      actionFetchComments()
      await actionComputeNavigation()
    } finally {
      Object.assign(loading.value, { course: false, module: false, lesson: false, comments: false })
    }
    return next
  }

  async function actionFetchCourse({ courseId }: { courseId: number }) {
    try {
      Object.assign(loading.value, { course: true })
      const queryParams = ["data[]=course", "data[]=modules"].join("&")
      const result = await serviceCourse.read(`/${courseId}/watch?${queryParams}`)
      course.value = result.course
      modules.value = result.modules
    } finally {
      // loading reset handled by caller
    }
  }

  async function actionNextModule({ modules: mods }: { modules: Module[] }) {
    const mod = fp.find((item: Module) => {
      return (item.lessons_completed_count ?? 0) < (item.lessons_count ?? 0)
    }, mods)
    return mod || mods[0]
  }

  async function actionFetchModuleLessons({ courseId, moduleId }: { courseId: number; moduleId: number }) {
    const queryParams = [
      "data[]=currentModuleLessons",
      moduleId ? `current_module_id=${moduleId}` : undefined,
    ].filter(Boolean).join("&")
    const { currentModuleLessons: lessons } = await serviceCourse.read(`/${courseId}/watch?${queryParams}`)
    currentModuleLessons.value = lessons
    currentModuleOpened.value = moduleId
  }

  async function actionComputeCurrentLesson({ orientation }: { orientation?: string }) {
    const lessons = currentModuleLessons.value
    switch (orientation) {
      case "first":
        return fp.first(lessons)
      case "last":
        return fp.last(lessons)
      default:
        return (
          fp.find((lesson: Lesson) => !lesson.is_completed, lessons) ||
          fp.last(lessons)
        )
    }
  }

  async function actionFetchLesson({ courseId, moduleId, lessonId }: { courseId: number; moduleId: number; lessonId: number }) {
    try {
      Object.assign(loading.value, { lesson: true })
      if (!courseId || !moduleId || !lessonId) {
        currentLesson.value = {}
      } else {
        const lesson = await serviceCourse.read(`/${courseId}/module/${moduleId}/lesson/${lessonId}`)
        currentLesson.value = lesson
      }
    } finally {
      // loading reset handled by caller
    }
  }

  async function actionFetchComments() {
    const { courseId, moduleId, lessonId } = params.value
    if (!courseId || !moduleId || !lessonId) return
    try {
      Object.assign(loading.value, { comments: true })
      const response = await serviceCourse.read(`${courseId}/module/${moduleId}/lesson/${lessonId}/comment`)
      lessonComments.value = response.reverse()
    } finally {
      // loading reset handled by caller
    }
  }

  async function actionCommentLike({ id }: { id: number }) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.updateId({
        id: `${courseId}/module/${moduleId}/lesson/${lessonId}/comment/${id}/like`,
      })
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionCommentUnlike({ id }: { id: number }) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.destroy({
        id: `${courseId}/module/${moduleId}/lesson/${lessonId}/comment/${id}/like`,
      })
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionCommentCreate({ parent_lesson_comment_id, comment }: { parent_lesson_comment_id?: number; comment: string }) {
    const { courseId, moduleId, lessonId } = params.value
    const data = {
      id: `${courseId}/module/${moduleId}/lesson/${lessonId}/comment`,
      comment,
      parent_lesson_comment_id,
    }
    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.postID(data)
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionCommentEdit({ id, comment }: { id: number; comment: string }) {
    const { courseId, moduleId, lessonId } = params.value
    const data = {
      id: `${courseId}/module/${moduleId}/lesson/${lessonId}/comment/${id}`,
      comment,
    }
    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.update(data)
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionCommentApprove({ id }: { id: number }) {
    try {
      Object.assign(loading.value, { comments: true })
      await serviceComments.postID({ id, status: "published" })
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionCommentDelete({ id }: { id: number }) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.destroy(
        `${courseId}/module/${moduleId}/lesson/${lessonId}/comment/${id}`
      )
      await actionFetchComments()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function actionFetchCoursePercentage({ courseId }: { courseId: number }) {
    const { lessons_completed, lessons_liberated_count } = await serviceCourse.read(`/${courseId}/progress`)
    if (!lessons_completed || !lessons_liberated_count) {
      coursePercentage.value = 0
      return
    }
    const percentage = parseInt(
      String(Math.min(100, (lessons_completed / lessons_liberated_count) * 100)),
      10
    )
    coursePercentage.value = percentage
  }

  function actionComputeNavigation() {
    const mods = modules.value
    const lessons = currentModuleLessons.value
    const { courseId, moduleId, lessonId } = params.value

    const lessonsFiltered = lessons.filter((lesson) => lesson.is_liberated === true)
    const moduleFiltered = mods.filter((mod) => mod.is_liberated === true)

    const currentModuleIndex = fp.findIndex((mod: Module) => mod.id == moduleId, moduleFiltered)
    const currentLessonIndex = fp.findIndex((lesson: Lesson) => lesson.id == lessonId, lessonsFiltered)

    const prev = lessonsFiltered[currentLessonIndex - 1]
    const prevMod = moduleFiltered[currentModuleIndex - 1]
    prevLesson.value =
      prev || prevMod
        ? {
            courseId,
            moduleId: prev?.module_id || prevMod?.id,
            lessonId: prev?.id,
          }
        : null

    const next = lessonsFiltered[currentLessonIndex + 1]
    const nextMod = moduleFiltered[currentModuleIndex + 1]
    nextLesson.value =
      next || nextMod
        ? {
            courseId,
            moduleId: next?.module_id || nextMod?.id,
            lessonId: next?.id,
          }
        : null
  }

  async function actionPostRating(rating: number) {
    const { courseId, moduleId, lessonId } = params.value
    await serviceCourse.postID({
      id: `${courseId}/module/${moduleId}/lesson/${lessonId}/rating`,
      rating,
    })
  }

  async function actionCompleteLesson() {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(submitting.value, { completion: true })
      const result = await serviceCourse.postID({
        id: `${courseId}/module/${moduleId}/lesson/${lessonId}/progress`,
        completed: true,
      })
      currentLesson.value = result.lesson
      await actionFetchCoursePercentage({ courseId: courseId! })
      await actionFetchModuleLessons({ courseId: courseId!, moduleId: moduleId! })
    } finally {
      Object.assign(submitting.value, { completion: false })
    }
  }

  async function actionCompleteState(completedState: unknown) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(submitting.value, { completion: true })
      const lesson = await serviceCourse.post(
        `${courseId}/module/${moduleId}/lesson/${lessonId}/completeState`,
        { id: lessonId, completedState }
      )
      currentLesson.value = lesson
    } finally {
      Object.assign(submitting.value, { completion: false })
    }
  }

  async function actionLoadSingleCertificate() {
    const { courseId } = params.value
    try {
      const response = await serviceCourse.read(`${courseId}/certificate/me`)
      if (response.showed === 0) {
        return response
      } else {
        return null
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function actionDisableCertificate() {
    try {
      await serviceCertificate.read("/notification")
    } catch (_error) { /* intentional */
    }
  }

  async function actionAnswerLessonQuiz(data: { question_id: number; [key: string]: unknown }) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(submitting.value, { answer: true })
      await serviceCourse.postID2({
        id: { id: `${courseId}/module/${moduleId}/lesson/${lessonId}/question/${data.question_id}/answer/student` },
        data,
      })
    } finally {
      Object.assign(submitting.value, { answer: false })
    }
  }

  async function actionClear() {
    clear()
  }

  async function actionTUSUpload({ location, file }: { location: string; file: File; metadata?: unknown }) {
    const Tus = (await import("tus-js-client")).default
    return new Promise<void>((resolve, reject) => {
      const upload = new Tus.Upload(file, {
        uploadUrl: location,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        chunkSize: 128 * MB_1,
        removeFingerprintOnSuccess: true,
        metadata: {
          filename: file.name,
          filetype: file.type,
        },
        onError: function (error) {
          reject(error)
        },
        onProgress: function (_bytesUploaded, _bytesTotal) {
          // progress tracking can be added here
        },
        onSuccess: function () {
          resolve()
        },
      })

      upload.findPreviousUploads().then(function (previousUploads) {
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0])
        }
        upload.start()
      })
    })
  }

  async function actionLoadSelect() {
    return await serviceCourse.read("/select")
  }

  async function actionFetchModule({ courseId, moduleId }: { courseId: number; moduleId: number }) {
    return serviceCourse.read(`${courseId}/module/${moduleId}`)
  }

  async function actionEnableTutor({ moduleId, state: enableState }: { moduleId: number; state: boolean }) {
    return tutorService.post(`by-module/${moduleId}/enable`, { enable: enableState })
  }

  async function actionUploadTutorFile({ moduleId, mediaIds }: { moduleId: number; mediaIds: number[] }) {
    return tutorService.post(`by-module/${moduleId}/files`, { media_ids: mediaIds })
  }

  async function actionFetchTutorFiles({ moduleId }: { moduleId: number }) {
    return tutorService.read(`by-module/${moduleId}/files`)
  }

  async function actionDeleteTutorFile({ moduleId, fileId }: { moduleId: number; fileId: number }) {
    return tutorService.destroy(`by-module/${moduleId}/files/${fileId}`)
  }

  async function actionSendTutorMessage({ moduleId, message, externalId }: { moduleId: number; message: string; externalId?: string }) {
    return tutorService.post(`by-module/${moduleId}/chat`, { message, externalId })
  }

  async function actionFetchTutorHistory({ moduleId }: { moduleId: number }) {
    return tutorService.read(`by-module/${moduleId}/history`)
  }

  async function actionRenameTutorHistory({ externalId, title }: { externalId: string; title: string }) {
    return tutorService.put(`history/${externalId}/rename`, { title, externalId })
  }

  async function actionDeleteTutorHistory({ externalId }: { externalId: string }) {
    return tutorService.destroy(`history/${externalId}`)
  }

  async function actionFetchTutorCurrentHistory({ moduleId }: { moduleId: number }) {
    return tutorService.read(`by-module/${moduleId}/history/last`)
  }

  async function actionGetTutorHistory({ externalId }: { externalId: string }) {
    return tutorService.read(`history/${externalId}`)
  }

  return {
    // State
    params,
    course,
    modules,
    currentModuleOpened,
    coursePercentage,
    currentLesson,
    currentModuleLessons,
    currentLessonComments,
    lessonComments,
    prevLesson,
    nextLesson,
    loading,
    submitting,
    currentCourseSelect,
    // Getters
    getCourse,
    getCoursePercentage,
    getModules,
    getCurrentModuleLessons,
    getCurrentLesson,
    getParams,
    getLoading,
    getSubmitting,
    getPrevLesson,
    getNextLesson,
    getLessonComments,
    getCurrentModuleOpened,
    getCurrentCourseSelect,
    // Actions
    actionCourseSelect,
    actionCurrentLessonOpened,
    actionOpenLesson,
    actionFetchCourse,
    actionNextModule,
    actionFetchModuleLessons,
    actionComputeCurrentLesson,
    actionFetchLesson,
    actionFetchComments,
    actionCommentLike,
    actionCommentUnlike,
    actionCommentCreate,
    actionCommentEdit,
    actionCommentApprove,
    actionCommentDelete,
    actionFetchCoursePercentage,
    actionComputeNavigation,
    actionPostRating,
    actionCompleteLesson,
    actionCompleteState,
    actionLoadSingleCertificate,
    actionDisableCertificate,
    actionAnswerLessonQuiz,
    actionClear,
    actionTUSUpload,
    actionLoadSelect,
    actionFetchModule,
    actionEnableTutor,
    actionUploadTutorFile,
    actionFetchTutorFiles,
    actionDeleteTutorFile,
    actionSendTutorMessage,
    actionFetchTutorHistory,
    actionRenameTutorHistory,
    actionDeleteTutorHistory,
    actionFetchTutorCurrentHistory,
    actionGetTutorHistory,
    clear,
  }
})
